import { useContext, useEffect, useRef, useState } from 'react';

import {
    Box,
    Button,
    DeviceMobile,
    DeviceMonitor,
    DeviceTV,
    FlexWrapper,
    FontIcon,
    HeroBlock,
    InputField,
    Separator,
    Text,
} from '@intigral/shared';
import { FlexChild } from '@intigral/shared/components/FlexWrapper';
import { useNavigate } from 'react-router-dom';
import users from '../../actions/users';
import { deviceTypes } from '../../config/constants';
import { EVENT, KEYS } from '../../contexts/MixpanelConstants';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import h from '../../helpers/common';
import f from '../../helpers/formatter';
import user from '../../helpers/user';
import { __ } from '../../translations';
import withPresetWidth from '@intigral/shared/components/hocs/withPresetWidth/withPresetWidth';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from './Modal';
import { useConfigurationSections } from '../../hooks/useConfiguration';
import { USER_SEGMENT } from '@intigral/constant/common';
import { isFreeTier } from '@intigral/utils/lib/user';

const BoxWidth = withPresetWidth(Box);
const FlexWrapperWithPresetWidth = withPresetWidth(FlexWrapper);

export const UserDevices = (props) => {
    const navigate = useNavigate();
    const [editMode, setEditMode] = useState(false);
    const [showRemoveDevices, setShowRemoveDevices] = useState(false);
    const [showMaxDevicePopup, setShowMaxDevicePopup] = useState(false);
    const [showRemoveDeviceWarning, setShowRemoveDeviceWarning] = useState(false);
    const [warningMsg, setWarningMsg] = useState(false);
    const [deletedDeviceGuids, setDeletedDeviceGuids] = useState([]);
    const [loadingDevice, setLoadingDevice] = useState(null);
    const queryClient = useQueryClient();
    // Using React Query instead of Redux for configuration
    const { tierConfig, deviceTranslations: deviceTranslationsConfig } = useConfigurationSections();
    const maxTeirRank = tierConfig?.maxTeirRank || 0;

    const eligibleToUpgradeSegments = tierConfig?.eligibleToUpgrade || [];
    const deviceTranslations = deviceTranslationsConfig || {};
    const deleteBtn = useRef([]);
    const removeBtn = useRef([]);
    const devicesList = useRef(null);
    const { track } = useContext(MixpanelContext) || {};
    const userInfo = user.getUserInfo();

    const scrollToRef = (ref) => window.scroll({ top: ref.current.offsetTop + 175, behavior: 'smooth' });

    useEffect(() => {
        if (props.maxdeviceReached) {
            track(EVENT.LOGIN.DEVICE_VIEW, { [KEYS.GENERAL.DEVICE_COUNT]: props.content?.length });
        }
        setShowMaxDevicePopup(props.maxdeviceReached);
    }, []);

    if (!props.content) {
        return null;
    }

    const removeDeviceMutation = useMutation({
        mutationFn: async ({ deviceGuid, deviceName }: { deviceGuid: string; deviceName: string }) => {
            setLoadingDevice(deviceGuid);
            const devicesGuids = [...deletedDeviceGuids];
            devicesGuids.push(deviceGuid);
            setDeletedDeviceGuids(devicesGuids);
            setShowRemoveDevices(true);
            setShowRemoveDeviceWarning(false);
            let result = false;
            const isMaxDevicesReached = props.maxdeviceReached;
            try {
                result = await users.removeDevice(deviceGuid, isMaxDevicesReached);
                return {
                    deviceGuid: deviceGuid,
                    isMaxDevicesReached: isMaxDevicesReached,
                    deviceName: deviceName,
                    deletionSuccess: result,
                };
            } catch (err) {
                const errMsg = err?.data?.code ? `err_msg_${err.data.code}` : 'error';
                if (isMaxDevicesReached) {
                    track(EVENT.LOGIN.DEVICE_DELETE_FAILED, {
                        [KEYS.GENERAL.DEVICE_NAME]: deviceName,
                        [KEYS.GENERAL.DEVICE_FAILURE]: __(errMsg),
                    });
                }
                setShowRemoveDeviceWarning(true);
                devicesGuids.splice(devicesGuids.indexOf(deviceGuid), 1);
                setWarningMsg(__(errMsg));
                return {
                    deviceGuid: deviceGuid,
                    isMaxDevicesReached: isMaxDevicesReached,
                    deviceName: deviceName,
                    deletionSuccess: result,
                };
            }
        },
        onSuccess: async ({ deviceGuid, isMaxDevicesReached, deviceName, deletionSuccess }) => {
            queryClient.invalidateQueries({ queryKey: ['user-profile'] });
            setLoadingDevice(null);
            if (deletionSuccess && isMaxDevicesReached) {
                track(EVENT.LOGIN.DEVICE_DELETE_SUCCESS, { [KEYS.GENERAL.DEVICE_NAME]: deviceName });
            }
        },
    });
    // const removeDevice = (deviceGuid, deviceName) => {
    //     removeDeviceMutation.mutate({ deviceGuid: deviceGuid, deviceName: deviceName });
    // };

    const removeDevice = (deviceGuid: string, deviceName: string, deviceFingerprint: string) => {
        if (
            deviceGuid === props.currentDeviceGuid ||
            (props.currentFingerprint && deviceFingerprint === props.currentFingerprint)
        ) {
            setWarningMsg(__('can_not_delete_current_device'));
            setShowRemoveDeviceWarning(true);
            return;
        }

        removeDeviceMutation.mutate({ deviceGuid, deviceName });
    };
    const closeMaxDevicePopup = () => {
        setShowMaxDevicePopup(false);
        setShowRemoveDevices(true);
        setEditMode(true);
        scrollToRef(devicesList);
    };

    const manageRemoveDevices = () => {
        setShowRemoveDevices(true);
        setEditMode(true);
    };

    const manageDevicesDone = () => {
        setShowRemoveDevices(false);
        setEditMode(false);
        setShowRemoveDeviceWarning(false);
    };

    const hasHigherTiersAndEligibleToUpgrade = () => {
        if (userInfo?.segment?.toUpperCase() === USER_SEGMENT.B2C) {
            const operator = (userInfo && 'operator' in userInfo && userInfo.operator.toUpperCase()) || null;
            const tierConfig = user.getUserTierConfig();
            const tierRank = (tierConfig && 'tierRank' in tierConfig && tierConfig.tierRank) || 0;
            if (isFreeTier(userInfo)) {
                return true;
            } else if (
                tierRank < maxTeirRank &&
                eligibleToUpgradeSegments.length &&
                operator &&
                eligibleToUpgradeSegments.includes(operator)
            ) {
                return true;
            }
        }
        return false;
    };
    const deviceTranslationsMap = (deviceTranslations, deviceLimit) => {
        if (Object.keys(deviceTranslations).length > 0 && deviceTranslations && deviceLimit) {
            if (h.getLang() === 'ar') {
                return 'ar' in deviceTranslations && deviceLimit === 1
                    ? ('ONE' in deviceTranslations.ar && deviceTranslations.ar.ONE) || ''
                    : ('MORE' in deviceTranslations.ar && deviceTranslations.ar.MORE) || '';
            } else {
                return 'en' in deviceTranslations && deviceLimit === 1
                    ? ('ONE' in deviceTranslations.en && deviceTranslations.en.ONE) || ''
                    : ('MORE' in deviceTranslations.en && deviceTranslations.en.MORE) || '';
            }
        }
        return null;
    };

    // updated part
    const getDeviceSVG = (deviceType) => {
        const iconName = Object.values(deviceTypes).map((item, index) => {
            if (Object.values(item).includes(deviceType)) {
                return Object.keys(deviceTypes)[index];
            }
        });
        // mapping deviceType with the proper icon
        switch (iconName[0]) {
            case 'WEB':
                return <DeviceMonitor />;
            case 'MOBILE':
                return <DeviceMobile />;
            case 'TABLET':
                return <DeviceMobile />;
            case 'TV':
                return <DeviceTV />;
            default:
                return <DeviceMonitor />;
        }
    };

    const isSTB = (deviceType) => (deviceType || '').toLowerCase().includes('stb');

    const PairButton = () => {
        const currentLanguage = h.getLang();
        if (props.meta.cta && props.meta.cta.is_enabled === 'yes') {
            return (
                <FlexChild alignSelf="center">
                    <Button LinkComponent="a" href={props.meta.cta.url[currentLanguage]} compact>
                        {props.meta.cta.title[currentLanguage]}
                    </Button>
                </FlexChild>
            );
        }
        return <></>;
    };

    const RemoveDeviceWarning = () => {
        return (
            <Box border="thick" theme="none">
                <FlexWrapper gap="2x" margin="1x" alignItems="center">
                    <FontIcon name="exclamation-mark" size="small" color="red" />
                    <Text as="p" variant="text5" color="red" alignment="start">
                        {warningMsg}
                    </Text>
                </FlexWrapper>
            </Box>
        );
    };

    if (!props.content) {
        return null;
    }

    return (
        <>
            <div className="container">
                <Separator color="dark" />
                <BoxWidth theme="light" padding={{ default: '2x', md: '6x' }} presetWidth="xlarge">
                    <FlexWrapper
                        flexDirection="row"
                        justifyContent="between"
                        alignItems="center"
                        marginBottom={{ default: '4x' }}
                    >
                        <Text as="h2" variant="h2" alignment="start" margin="0" fontWeight="medium">
                            {__('devices_control_title')}
                        </Text>
                        {editMode ? (
                            <Text as="p" size="14" onClick={(e) => manageDevicesDone()} color="brand">
                                <a>{__('done')}</a>
                            </Text>
                        ) : (
                            <Text as="p" size="14" onClick={(e) => manageRemoveDevices()} color="brand">
                                <a>{__('devices_control_manage_your_devices')}</a>
                            </Text>
                        )}
                    </FlexWrapper>
                    {props.maxdeviceReached && (
                        <HeroBlock
                            heading={
                                <Text as="h2" variant="h2" alignment="center" margin="4x" size="32">
                                    {__('premium_word')}
                                </Text>
                            }
                            picture={h.getAppConfig()?.paidVsFree?.maxDeviceLimitReachBGImage}
                            compact
                            minHeight
                            theme={'light'}
                        >
                            <FlexWrapper flexDirection="column" gap="4x" marginBottom={{ default: '4x' }}>
                                {hasHigherTiersAndEligibleToUpgrade() ? (
                                    <>
                                        <Text as="p" size="16" alignment="center" color="gray">
                                            {__(`max_devices_reached_desc`)}
                                        </Text>
                                        <FlexChild alignSelf="center">
                                            <Button compact LinkComponent={'a'} href={user.getMyAccountUrl()}>
                                                {__('subscribe_to_premium')}
                                            </Button>
                                        </FlexChild>
                                    </>
                                ) : (
                                    <Text as="p" size="16" alignment="center" color="gray">
                                        {__(`max_devices_reached_desc_premium`)}
                                    </Text>
                                )}
                            </FlexWrapper>
                        </HeroBlock>
                    )}
                    <FlexWrapper flexDirection="column" gap="4x">
                        {props.content !== null && props.content.length > 0 ? (
                            <>
                                {showRemoveDeviceWarning ? <RemoveDeviceWarning /> : ''}
                                <div ref={devicesList} className={`devices ${editMode ? 'devices_edit' : ''}`}>
                                    {props.content.map((item, index) => {
                                        const currentDevice = item.deviceGuid === props.currentDeviceGuid;
                                        return (
                                            <div
                                                key={item.deviceGuid}
                                                className={`p-4 rounded-md ${currentDevice ? 'bg-green-50' : ''}`}
                                            >
                                                <FlexWrapper
                                                    flexDirection="row"
                                                    justifyContent="between"
                                                    alignItems="center"
                                                >
                                                    <FlexWrapper flexDirection="row" alignItems="center" gap="2x">
                                                        {getDeviceSVG(item.deviceType)}
                                                        <FlexWrapper alignItems="start" flexDirection="column">
                                                            <Text
                                                                as="p"
                                                                alignment="start"
                                                                fontWeight="medium"
                                                                margin="0"
                                                            >
                                                                {item.deviceName}
                                                            </Text>
                                                            <Text as="span" size="12" color="gray" alignment="start">
                                                                {__('devices_control_device_added_date')}{' '}
                                                                {f.formatDate(item.addedTime)}
                                                            </Text>
                                                        </FlexWrapper>
                                                    </FlexWrapper>

                                                    {currentDevice ? (
                                                        <FlexWrapper flexDirection="row" alignItems="center" gap="1x">
                                                            <FontIcon
                                                                name="check-circle"
                                                                size="smaller"
                                                                color="green"
                                                            />
                                                            {/* <Text as="span" size="12" color="green">
                                                                {__('devices_control_current')}
                                                            </Text> */}
                                                        </FlexWrapper>
                                                    ) : (
                                                        <div
                                                            ref={(el) => {
                                                                removeBtn.current[item.deviceGuid] = el;
                                                            }}
                                                        >
                                                            <Button
                                                                loading={
                                                                    loadingDevice == item.deviceGuid &&
                                                                    removeDeviceMutation.isPending
                                                                }
                                                                plain
                                                                onClick={() =>
                                                                    !isSTB(item.deviceType) &&
                                                                    !deletedDeviceGuids.includes(item.deviceGuid) &&
                                                                    removeDevice(
                                                                        item.deviceGuid,
                                                                        item.deviceName,
                                                                        item.deviceUniqueIdentifier
                                                                    )
                                                                }
                                                            >
                                                                <FontIcon name="basket" size="smaller" color="red" />
                                                            </Button>
                                                        </div>
                                                    )}
                                                </FlexWrapper>
                                                {index !== props.content.length - 1 && <Separator color="light" />}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <p className="profile-desc">{__('devices_control_no_devices')}</p>
                        )}
                        <PairButton />
                    </FlexWrapper>
                </BoxWidth>
                <Separator color="dark" margin="4x" />
            </div>

            {/* <div className="overlay-main" style={{ display: showMaxDevicePopup ? 'flex' : 'none' }}> */}
            {/* <section className="overlay-content">
                    <div className="overlay-close">
                        <span onClick={(e) => closeMaxDevicePopup()} className="icon-close" />
                    </div> */}
            <Modal
                useFlexLayout={false}
                open={showMaxDevicePopup}
                onOpenChange={closeMaxDevicePopup}
                closePlacement="end"
                widthSet="medium"
            >
                {/* <div className="container"> */}
                <FlexWrapperWithPresetWidth presetWidth="medium" flexDirection="column" gap={'3x'}>
                    <FontIcon size="size60" md="size60" name="exclamation-mark" color="red" />
                    <Text as="h2" variant="h2" margin={'0'}>
                        {__('devices_control_popup_title')}
                    </Text>
                    {hasHigherTiersAndEligibleToUpgrade() ? (
                        <>
                            <Text as="p" variant="text3" color="gray">
                                {__('tier_devices_control_popup_description')}
                            </Text>
                            <Button compact LinkComponent={'a'} href={user.getMyAccountUrl()}>
                                {' '}
                                {__('tier_devices_control_popup_cta')}
                            </Button>
                        </>
                    ) : (
                        <Text as="p" variant="text3" color="gray">
                            {__('devices_control_popup_description')}
                        </Text>
                    )}
                    {/* <Button compact className="overlay-btn-close" onClick={(e) => closeMaxDevicePopup()}>
                                {__('devices_control_popup_button')}
                            </Button> */}
                    {showRemoveDeviceWarning ? <RemoveDeviceWarning /> : ''}
                    <div ref={devicesList}>
                        {props.content.map((item, index) => {
                            const currentDevice = item.deviceGuid === props.deviceGuid;

                            return (
                                <InputField
                                    preIcon={getDeviceSVG(item.deviceType)}
                                    cta={
                                        currentDevice ? (
                                            <FlexWrapper flexDirection="row" alignItems="center" gap="1x">
                                                <FontIcon name="check-circle" color="green" />
                                                <Text as="span" size="12" color="green">
                                                    {__('devices_control_current')}
                                                </Text>
                                            </FlexWrapper>
                                        ) : (
                                            <Button
                                                loading={
                                                    loadingDevice == item.deviceGuid && removeDeviceMutation.isPending
                                                }
                                                plain
                                                onClick={() => {
                                                    if (
                                                        !isSTB(item.deviceType) &&
                                                        !deletedDeviceGuids.includes(item.deviceGuid)
                                                    ) {
                                                        removeDevice(
                                                            item.deviceGuid,
                                                            item.deviceName,
                                                            item.deviceUniqueIdentifier
                                                        );
                                                    }
                                                }}
                                            >
                                                <FontIcon name="basket" color="red" />
                                            </Button>
                                        )
                                    }
                                    label={
                                        <FlexWrapper flexDirection="column">
                                            <Text
                                                as="p"
                                                alignment="start"
                                                truncate
                                                color={currentDevice ? 'green' : 'white'}
                                            >
                                                {item.deviceName}
                                            </Text>
                                            <Text as="p" alignment="start" truncate variant="text5" color="gray">
                                                {__('devices_control_device_added_date')} {f.formatDate(item.addedTime)}
                                            </Text>
                                        </FlexWrapper>
                                    }
                                />
                            );
                        })}
                    </div>
                    <Button LinkComponent="a" href={props.meta.cta.url[h.getLang()]} compact>
                        {props.meta.cta.title[h.getLang()]}
                    </Button>
                </FlexWrapperWithPresetWidth>
                {/* </div> */}
            </Modal>
            {/* </section> */}
            {/* </div> */}
        </>
    );
};
